import { useModuleAccess } from "@/hooks/useModuleAccess";
import { AppLayout } from "@/components/AppLayout";
import { ProfileHeader } from "@/components/ProfileHeader";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetEmployee } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ProfileAboutTab } from "@/components/profile/ProfileAboutTab";
import { ProfilePersonalInfoTab } from "@/components/profile/ProfilePersonalInfoTab";
import { ProfileSalaryTab } from "@/components/profile/ProfileSalaryTab";
import { ProfileDocumentsTab } from "@/components/profile/ProfileDocumentsTab";
import { ProfileTimeOffTab } from "@/components/profile/ProfileTimeOffTab";
import { ProfileNotesTab } from "@/components/profile/ProfileNotesTab";
import { ProfileAuditTab } from "@/components/profile/ProfileAuditTab";
import { ProfilePerformanceTab } from "@/components/profile/ProfilePerformanceTab";
import {
  ArrowLeft, User, FileText, DollarSign, FolderOpen, CalendarDays, MessageSquare, History, BarChart3
} from "lucide-react";
import { ProfileHeaderSkeleton, CardGridSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";


export default function EmployeeProfile() {
  const { t } = useTranslation();
  const { canAccess } = useModuleAccess();
  const { user } = useAuth();
  const { id } = useParams<{ id: string }>();
  const isAdmin = user?.role.mainRole === "admin";
  const isHr = user?.role.mainRole === "hr";

  const { data: employee, isLoading } = useQuery({
    queryKey: ["employee", id],
    queryFn: () => apiGetEmployee(id!),
    enabled: !!id,
  });

  const isLineManagerOfEmployee = !!(
    user?.isLineManager &&
    user?.employeeId &&
    employee?.reportsTo?.id === user.employeeId
  );

  const isLineManagerWithAccess = isLineManagerOfEmployee && user?.fullProfileAccess !== false;
  // HR/admin see everything; line managers see only personal info (restricted) + salary
  const canSeeFullProfile = isAdmin || isHr;
  const canSeeRestrictedProfile = isLineManagerWithAccess;
  const canManage = canSeeFullProfile || isLineManagerWithAccess;

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-6 w-full">
          <ProfileHeaderSkeleton />
          <div className="flex gap-2 border-b border-border/60 pb-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-24" />
            ))}
          </div>
          <CardGridSkeleton count={6} />
        </div>
      </AppLayout>
    );
  }

  if (!employee) {
    return (
      <AppLayout>
        <div className="text-center py-20">
          <p className="text-lg font-medium text-foreground mb-2">{t("employees.notFound")}</p>
          <p className="text-sm text-muted-foreground mb-6">{t("employees.notFoundDesc")}</p>
          <Link
            to="/employees"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("employees.backToEmployees")}
          </Link>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6 w-full">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <Link
            to="/employees"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            {t("employees.backToEmployees")}
          </Link>
        </div>

        <ProfileHeader employee={employee} />

        <Tabs defaultValue="about" className="w-full">
          <TabsList className="w-full justify-start bg-transparent h-auto p-0 border-b border-border/60 rounded-none gap-0 overflow-x-auto overflow-y-hidden">
            <TabsTrigger
              value="about"
              className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
            >
              <User className="h-3.5 w-3.5" />
              {t("profile.tabs.about")}
            </TabsTrigger>
            {(canSeeFullProfile || canSeeRestrictedProfile) && (
              <>
                <TabsTrigger
                  value="personal"
                  className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {t("profile.tabs.personalInfo")}
                </TabsTrigger>
                <TabsTrigger
                  value="performance"
                  className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
                >
                  <BarChart3 className="h-3.5 w-3.5" />
                  {t("profile.tabs.performance")}
                </TabsTrigger>
                {canAccess("salary") && <TabsTrigger
                  value="salary"
                  className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
                >
                  <DollarSign className="h-3.5 w-3.5" />
                  {t("profile.tabs.salary")}
                </TabsTrigger>}
              </>
            )}
            {canSeeFullProfile && (
              <TabsTrigger
                value="documents"
                className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <FolderOpen className="h-3.5 w-3.5" />
                {t("profile.tabs.documents")}
              </TabsTrigger>
            )}
            {(canSeeFullProfile || canSeeRestrictedProfile) && (
              <TabsTrigger
                value="timeoff"
                className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <CalendarDays className="h-3.5 w-3.5" />
                {t("profile.tabs.timeOff")}
              </TabsTrigger>
            )}
            {canSeeFullProfile && (
              <TabsTrigger
                value="notes"
                className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                {t("profile.tabs.notes")}
              </TabsTrigger>
            )}
            {isAdmin && (
              <TabsTrigger
                value="audit"
                className="gap-1.5 text-sm rounded-none border-b-2 border-transparent px-4 py-2.5 -mb-px data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:shadow-none text-muted-foreground hover:text-foreground transition-colors bg-transparent"
              >
                <History className="h-3.5 w-3.5" />
                {t("profile.tabs.auditLog")}
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="about" className="mt-4">
            <ProfileAboutTab employee={employee} isPublicOnly={!canSeeFullProfile && !canSeeRestrictedProfile} />
          </TabsContent>

          {(canSeeFullProfile || canSeeRestrictedProfile) && (
            <>
              <TabsContent value="personal" className="mt-4">
                <ProfilePersonalInfoTab employee={employee} isOwnProfile={false} isLineManagerOfEmployee={isLineManagerOfEmployee} />
              </TabsContent>

              <TabsContent value="performance" className="mt-4">
                <ProfilePerformanceTab employee={employee} canManage={canManage} />
              </TabsContent>

              {canAccess("salary") && <TabsContent value="salary" className="mt-4">
                <ProfileSalaryTab employee={employee} isOwnProfile={false} isLineManagerOfEmployee={isLineManagerOfEmployee} />
              </TabsContent>}
            </>
          )}

          {canSeeFullProfile && (
            <>
              <TabsContent value="documents" className="mt-4">
                <ProfileDocumentsTab employee={employee} />
              </TabsContent>

              <TabsContent value="notes" className="mt-4">
                <ProfileNotesTab employee={employee} />
              </TabsContent>
            </>
          )}

          {(canSeeFullProfile || canSeeRestrictedProfile) && (
            <TabsContent value="timeoff" className="mt-4">
              <ProfileTimeOffTab employee={employee} />
            </TabsContent>
          )}

          {isAdmin && (
            <TabsContent value="audit" className="mt-4">
              <ProfileAuditTab employee={employee} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </AppLayout>
  );
}
